import EventAgent from '@hotmart/event-agent-js';
import { sendEvent } from 'src/utils/datahubUtils';
import { IDefaultSchema, DatahubEntity, DatahubAction, CaptureEvent, WatchtimeAction } from 'src/interfaces/Datahub';
import projectConf from 'src/../package.json';
import { IApplicationData } from 'src/interfaces/ApplicationData';
import TrackingService from '../TrackingService';
import Watchtime from './entities/watchtime';
import Firstplay from './entities/firstplay';
import Subtitle from './entities/subtitle';
import Fullscreen from './entities/fullscreen';
import PlaybackSpeed from './entities/playbackSpeed';
import Autoplay from './entities/autoplay';
import Resolution from './entities/resolution';
import Playback from './entities/playback';
import Volume from './entities/volume';
import Marker from './entities/marker';
import Seeked from './entities/seeked';
import PictureInPicture from './entities/pictureInPicture';
import LoadedMetadata from './entities/loadedmetadata';
import Error from './entities/error';
import SubtitleStyle from './entities/subtitleStyle';
import SchemaValidationError from './entities/schemaValidationError';

export default class DatahubService extends TrackingService<IDefaultSchema> {
  entities = {
    firstplay: {} as Firstplay,
    watchtime: {} as Watchtime,
    subtitle: {} as Subtitle,
    fullscreen: {} as Fullscreen,
    playback_speed: {} as PlaybackSpeed,
    autoplay: {} as Autoplay,
    resolution: {} as Resolution,
    playback: {} as Playback,
    volume: {} as Volume,
    marker: {} as Marker,
    seeked: {} as Seeked,
    picture_in_picture: {} as PictureInPicture,
    loadedmetadata: {} as LoadedMetadata,
    error: {} as Error,
    subtitle_style: {} as SubtitleStyle,
  };

  schemaValidationError = new SchemaValidationError();

  constructor(applicationData: IApplicationData, viewId: string) {
    super();
    EventAgent.mode = process.env.APP_ENV as typeof EventAgent.mode;

    this.data = {
      applicationKey: applicationData.applicationKey,
      duration: applicationData.mediaDuration,
      media: applicationData.mediaCode,
      user: applicationData.userCode ?? '',
      viewId,
      playerVersion: projectConf.version,
      transmissionType: applicationData.transmissionType,
      mediaType: applicationData.mediaType,
      isMediaPublic: applicationData.isMediaPublic,
    };

    if (applicationData.experimentName) {
      this.data.experimentName = applicationData.experimentName;
    }

    this.entities.firstplay = new Firstplay(this.data);
    this.entities.watchtime = new Watchtime(this.data);
    this.entities.subtitle = new Subtitle(this.data);
    this.entities.fullscreen = new Fullscreen(this.data);
    this.entities.playback_speed = new PlaybackSpeed(this.data);
    this.entities.autoplay = new Autoplay(this.data);
    this.entities.resolution = new Resolution(this.data);
    this.entities.playback = new Playback(this.data);
    this.entities.volume = new Volume(this.data);
    this.entities.marker = new Marker(this.data);
    this.entities.seeked = new Seeked(this.data);
    this.entities.picture_in_picture = new PictureInPicture(this.data);
    this.entities.loadedmetadata = new LoadedMetadata(this.data);
    this.entities.error = new Error(this.data);
    this.entities.subtitle_style = new SubtitleStyle(this.data);
  }

  validateEventSchema(entity: DatahubEntity, action: DatahubAction, data?: unknown) {
    this.entities[entity?.toLowerCase()]?.schema?.[action?.toLowerCase()]
      .validate(data, { abortEarly: false })
      .catch((error) => {
        const data = {
          entity,
          action,
          errors: error.errors.join(' AND '),
          value: JSON.stringify(error.value),
        };
        this.schemaValidationError.sendEvent(data);
      });
  }

  track(entity: DatahubEntity, action: DatahubAction, data?: unknown) {
    this.entities[entity?.toLowerCase()][action?.toLowerCase()](this.captureEvent, data);
  }

  shouldCaptureEvent(entity: DatahubEntity, action: DatahubAction, data: unknown) {
    if (entity === DatahubEntity.WATCHTIME && action === WatchtimeAction.MEASURE) return true;
    return !this.isEventDuplicated({ entity, action, data } as unknown as IDefaultSchema);
  }

  captureEvent: CaptureEvent<DatahubAction> = (entity, action, data) => {
    if (this.shouldCaptureEvent(entity, action, data)) {
      sendEvent(entity, action, data);

      // sparkle web is an application that will be discontinued in the near future, than let's not validate schema data
      const SPARKLE_APPLICATION_KEY = '4e2bf133-80ff-4cc2-8699-45a53769cb93';
      if (this.data.applicationKey === SPARKLE_APPLICATION_KEY) return;

      this.validateEventSchema(entity, action, data.event);
    }
  };
}
